import { createMongoAbility } from "@casl/ability";
import { AuthPayload } from "./models";

export type ObjectFromList<T extends ReadonlyArray<string>, V = string> = {
  [K in T extends ReadonlyArray<infer U> ? U : never]: V;
};

export const getInitialFromResponse = (res: IPermResponse[]): IPerm => {
  const perm: any = {};
  res.forEach((key) => {
    const subject: Subjects = key.subject;
    if (!perm[subject]) perm[subject] = [];
    if (Array.isArray(key.action))
      perm[subject] = perm[subject]?.concat(key.action);
    else perm[subject]?.push(key.action);
  });
  return perm;
};

export const subjectList = [
  "Address",
  "Car",
  "Calibration",
  "Category",
  "City",
  "Country",
  "Event",
  "Investor",
  "InvestObject",
  "InvestGroup",
  "Location",
  "Machine",
  "Object",
  "Order",
  "Payment",
  "Payout",
  "Permission",
  "Product",
  "Provider",
  "Role",
  "Route",
  "Service",
  "SnackAddress",
  "SnackItem",
  "Space",
  "Store",
  "Task",
  "Template",
  "User",
  "Visit",
  "all",
] as const;

export type IPerm = ObjectFromList<typeof subjectList, string | string[]>;

export type Subjects = (typeof subjectList)[number];
export type Actions =
  | "manage"
  | "cancel"
  | "create"
  | "read"
  | "upload"
  | "start"
  | "in-progress"
  | "update"
  | "set-category"
  | "delete"
  | "conduct"
  | "create-document"
  | "create-order"
  | "stop-list"
  | "create-place"
  | "move-to"
  | "add-to"
  | "total-price"
  | "add-event";

export type IFormPerm = {
  name: keyof IPerm;
  title: string;
  perm: { name: string; value: Actions }[];
};

export type IFormPermGroup = {
  title: string;
  perm: (keyof IPerm)[];
};

export const formPermGroup: IFormPermGroup[] = [
  {
    title: "Objects",
    perm: ["Object"],
  },
  {
    title: "Snack",
    perm: ["SnackAddress", "SnackItem"],
  },
  {
    title: "Visits",
    perm: ["Visit"],
  },
  {
    title: "Routes",
    perm: ["Route"],
  },
  {
    title: "Orders",
    perm: ["Order"],
  },
  {
    title: "Services",
    perm: ["Service"],
  },
  {
    title: "Calibration",
    perm: ["Calibration"],
  },
  {
    title: "Locations",
    perm: ["Location", "Country", "City"],
  },
  {
    title: "Equipment",
    perm: ["Machine", "Payment", "Car", "Event"],
  },
  {
    title: "Stock",
    perm: ["Product", "Category", "Provider", "Template"],
  },
  {
    title: "Documents",
    perm: ["Store"],
  },
  {
    title: "Invest",
    perm: ["InvestObject", "Payout"],
  },
  {
    title: "Users",
    perm: ["User", "Role"],
  },
];

export const formPerm: IFormPerm[] = [
  {
    name: "Car",
    title: "Cars",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
      { name: "Add event", value: "add-event" },
      { name: "Add TO", value: "add-to" },
    ],
  },
  {
    name: "Calibration",
    title: "Calibration",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "Category",
    title: "Categories",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "City",
    title: "Cities",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "Country",
    title: "Countries",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "Event",
    title: "Events",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "Investor",
    title: "Investors",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "InvestObject",
    title: "Invest Objects",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "Location",
    title: "Locations",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
      { name: "Create place", value: "create-place" },
    ],
  },
  {
    name: "Machine",
    title: "Machines",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
      { name: "Add event", value: "add-event" },
      { name: "Move to", value: "move-to" },
    ],
  },
  {
    name: "Object",
    title: "Objects",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
      { name: "Stop list", value: "stop-list" },
      { name: "Set category", value: "set-category" },
    ],
  },
  {
    name: "Order",
    title: "Orders",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "In progress", value: "in-progress" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "Payment",
    title: "Payments",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
      { name: "Add event", value: "add-event" },
      { name: "Move to", value: "move-to" },
    ],
  },
  {
    name: "Payout",
    title: "Payouts",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "Permission",
    title: "Permissions",
    perm: [{ name: "Update", value: "update" }],
  },
  {
    name: "Product",
    title: "Products",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
      { name: "Total Price", value: "total-price" },
      { name: "Create document", value: "create-document" },
    ],
  },
  {
    name: "Provider",
    title: "Providers",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "Role",
    title: "Roles",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "Route",
    title: "Routes",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "Service",
    title: "Services",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "SnackAddress",
    title: "Object",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
      { name: "Upload", value: "upload" },
    ],
  },
  {
    name: "SnackItem",
    title: "Product",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "Store",
    title: "Documents",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
      { name: "Conduct", value: "conduct" },
      { name: "Cancel", value: "cancel" },
    ],
  },
  {
    name: "Template",
    title: "New installations",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Create order", value: "create-order" },
      { name: "Write-off", value: "create-document" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "User",
    title: "Users",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "Visit",
    title: "Visits",
    perm: [
      { name: "Read", value: "read" },
      { name: "Start", value: "start" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
  {
    name: "Task",
    title: "Tasks",
    perm: [
      { name: "Read", value: "read" },
      { name: "Create", value: "create" },
      { name: "Update", value: "update" },
      { name: "Delete", value: "delete" },
    ],
  },
];

export type IPermOwner = {
  spaceId?: number;
  ownerId: number;
  ownerType: "USER" | "ROLE";
};

export type IPermRequest = {
  owner: IPermOwner;
  data: IPerm;
};

export type IPermResponse = {
  action: Actions | Actions[];
  subject: Subjects;
};
/**?
 * 
 * SubjectRawRule<
  string,
  ExtractSubjectType<Subjects>,
  MongoQuery
>
 */
export const mapData: IPermResponse[] = [];

export default function defineAbilityFor(
  user: AuthPayload,
  mapData: IPermResponse[],
) {
  return createMongoAbility<[Actions, Subjects]>(mapData);
}

export type AbilityType = ReturnType<typeof defineAbilityFor>;
